import { c as _c } from "react/compiler-runtime";
import { InfoBar } from "@components/common/info-bar/info-bar";
import { InfoBarCounts } from "@components/common/info-bar/info-bar-counts";
import { EditableGroupName } from "@components/group-details/editable-group-name";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useMediaQueryList } from "@hooks/use-media-query";
import { InviteMemberToGroup } from "@pages/group-details/invite-member-to-group";
import { GroupTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { UiUtils } from "@stellar/utils";
import { groupProjectsCountSelector, selectedGroupMembersSelector } from "@store/groups/groups-selector";
import { useAppSelector } from "@store/store-helper";
import { countGroupMembersByRole, getProjectNumbersInGroup } from "@utils/group-utils";
/** The role counts of the group's members. */
function getMemberCounts(members, canAssignManagers) {
  const roleCounts = countGroupMembersByRole(members);

  // Workspaces that cannot assign managers only ever have group viewers
  if (!canAssignManagers) {
    return [{
      label: "Viewers",
      value: roleCounts.viewers
    }];
  }
  return [{
    label: "Group Managers",
    value: roleCounts.groupManagers
  }, {
    label: "Project Managers",
    value: roleCounts.projectManagers
  }, {
    label: "Viewers",
    value: roleCounts.viewers
  }];
}

/** The number of active and archived projects of the group. */
function getProjectCounts(group, totalProjectsCount) {
  const activeProjectsCount = getProjectNumbersInGroup(group);
  return [{
    label: "Active",
    value: activeProjectsCount
  }, {
    label: "Archived",
    // The two counts come from separate requests, so a stale pair must not show a negative.
    value: totalProjectsCount === undefined ? UiUtils.DASH : Math.max(totalProjectsCount - activeProjectsCount, 0)
  }];
}

/**
 * Info bar for the group details tabs
 */
export function GroupDetailsInfoBar(t0) {
  const $ = _c(30);
  const {
    group,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const {
    groupId,
    groupTabs
  } = useAppParams();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const selectedGroupMembers = useAppSelector(selectedGroupMembersSelector);
  const t2 = groupId ?? "";
  let t3;
  if ($[0] !== t2) {
    t3 = groupProjectsCountSelector(t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const totalProjectsCount = useAppSelector(t3);
  const t4 = group ?? null;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      selectedGroup: t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    canEditGroupDetails,
    canInviteUsersToGroup
  } = useHasUserValidRoleGroupLevel(t5);
  if (!group || isLoading) {
    let t6;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <InfoBar isLoading={true} />;
      $[4] = t6;
    } else {
      t6 = $[4];
    }
    return t6;
  }
  let t6;
  if ($[5] !== canEditGroupDetails || $[6] !== group.name) {
    t6 = {
      label: "Group",
      isFlexible: true,
      content: <EditableGroupName name={group.name} isEditable={canEditGroupDetails} />
    };
    $[5] = canEditGroupDetails;
    $[6] = group.name;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== canAssignManagers || $[9] !== selectedGroupMembers) {
    t7 = getMemberCounts(selectedGroupMembers, canAssignManagers);
    $[8] = canAssignManagers;
    $[9] = selectedGroupMembers;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = {
      label: "Members",
      isFlexible: true,
      content: <InfoBarCounts counts={t7} />
    };
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== group || $[14] !== totalProjectsCount) {
    t9 = getProjectCounts(group, totalProjectsCount);
    $[13] = group;
    $[14] = totalProjectsCount;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = {
      label: "Projects",
      isFlexible: true,
      content: <InfoBarCounts counts={t9} />
    };
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6 || $[20] !== t8) {
    t11 = [t6, t8, t10];
    $[18] = t10;
    $[19] = t6;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== group) {
    t12 = <InviteMemberToGroup group={group} />;
    $[22] = group;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = groupTabs !== GroupTabs.managers || !canInviteUsersToGroup || isScreenSmOrSmaller;
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = {
      content: t12,
      isHidden: t13
    };
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t11 || $[28] !== t14) {
    t15 = <InfoBar singleRowFrom="lg" items={t11} action={t14} />;
    $[27] = t11;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}